import React from 'react';
import { Box } from '@mui/material';

/**
 * One metric by day as an SVG line with the in-range band shaded — a moving band (EWMA's path, by
 * block end date) when given, else a flat one; today's point hollow when projected, with a solid
 * dot at today's actual so far. A clear trend is drawn as a dashed fitted line in its colour.
 */
export default function Sparkline({ points, band, path: movingBand, current, trend }: {
  points: Array<{ date: string; value: number | null }>;
  band: { nearLow: number | null; nearHigh: number | null };
  path?: Array<{ end: string; nearLow: number; nearHigh: number }> | null;
  current: { date: string; value: number | null; projected: boolean; soFar?: number | null };
  trend?: { start: { date: string; value: number }; end: { date: string; value: number }; color: string } | null;
}) {
  const w = 120; const h = 30; const pad = 3;
  const vals = [...points.map(p => p.value), current.soFar ?? null].filter((v): v is number => v != null);
  if (vals.length < 2) return <Box sx={{ width: w, height: h }} />;
  const byEnd = movingBand?.length ? new Map(movingBand.map(p => [p.end, p])) : null;
  const moving = byEnd ? points.map((p, i) => ({ i, b: byEnd.get(p.date) })).filter((q): q is { i: number; b: { end: string; nearLow: number; nearHigh: number } } => !!q.b) : [];
  const bandLo = moving.length ? Math.min(...moving.map(q => q.b.nearLow)) : band.nearLow;
  const bandHi = moving.length ? Math.max(...moving.map(q => q.b.nearHigh)) : band.nearHigh;
  const lo = Math.min(...vals, bandLo ?? Infinity);
  const hi = Math.max(...vals, bandHi ?? -Infinity);
  const span = hi - lo || 1;
  const x = (i: number) => pad + (i / (points.length - 1)) * (w - 2 * pad);
  const y = (v: number) => h - pad - ((v - lo) / span) * (h - 2 * pad);
  const path = points.map((p, i) => (p.value == null ? null : `${i === 0 || points[i - 1].value == null ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`)).filter(Boolean).join(' ');
  const last = points.length - 1;
  const cur = points[last];
  const isCurrent = cur.date === current.date && cur.value != null;
  const trendStart = trend ? points.findIndex(p => p.date === trend.start.date) : -1;
  const trendEnd = trend ? points.findIndex(p => p.date === trend.end.date) : -1;
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      {moving.length > 1 ? (
        <polygon points={[...moving.map(q => `${x(q.i).toFixed(1)},${y(q.b.nearHigh).toFixed(1)}`), ...[...moving].reverse().map(q => `${x(q.i).toFixed(1)},${y(q.b.nearLow).toFixed(1)}`)].join(' ')} fill="#10B981" opacity={0.12} />
      ) : band.nearLow != null && band.nearHigh != null && (
        <rect x={0} y={y(band.nearHigh)} width={w} height={Math.max(1, y(band.nearLow) - y(band.nearHigh))} fill="#10B981" opacity={0.12} />
      )}
      <path d={path} fill="none" stroke="#2563EB" strokeWidth={1.2} />
      {trend && trendStart >= 0 && trendEnd > trendStart && (
        <line x1={x(trendStart)} y1={y(Math.min(hi, Math.max(lo, trend.start.value)))} x2={x(trendEnd)} y2={y(Math.min(hi, Math.max(lo, trend.end.value)))} stroke={trend.color} strokeWidth={1.2} strokeDasharray="3 2" opacity={0.45} />
      )}
      {isCurrent && (
        <circle cx={x(last)} cy={y(cur.value!)} r={3} fill={current.projected ? '#fff' : '#111827'} stroke="#111827" strokeWidth={1.5} />
      )}
      {isCurrent && current.projected && current.soFar != null && (
        <circle cx={x(last)} cy={y(current.soFar)} r={1.8} fill="#111827" />
      )}
    </svg>
  );
}
